import type {DigitalEditionData, DigitalEditionPositionsData, ProductData} from '../../types/types.ts';
import type {ContentAdapter} from '../adapters/content-adapter.ts';
import type {Store} from '../lib/store.ts';
import type {ReaderListing} from '../pages/reader/reader-listing.ts';
import {Article} from './article.ts';
import {ReaderEdition, type ReaderItem} from './reader-edition.ts';

export class DigitalEdition extends ReaderEdition {
	editionData: DigitalEditionData;
	positionsData: DigitalEditionPositionsData;
	productData: ProductData;

	constructor(
		editionData: DigitalEditionData,
		positionsData: DigitalEditionPositionsData,
		productData: ProductData,
		adapter: ContentAdapter
	) {
		super(adapter);
		this.editionData = editionData;
		this.positionsData = positionsData;
		this.productData = productData;
		this.productType = productData.attributes.productType;
		this.editionType =
			productData.attributes.productType === 'magazine' ? 'magazine-edition' : 'front-page-edition';

		this.id = editionData.id;
		this.title = editionData.attributes.title;
		this.publishDate = editionData.attributes.lastPublishedAt || editionData.attributes.createdAt;
		this.productId = productData.id;
		this.productName = productData.attributes.name;
		this.productTitle = productData.attributes.title;
	}

	getSection(listing: ReaderListing) {
		return listing.item.section;
	}

	async loadArticleList(store: Store): Promise<ReaderItem[]> {
		const articleIds = this.positionsData.positions.flatMap((position) => {
			if ('item' in position) {
				return position.item.data.id;
			}

			if ('items' in position) {
				return position.items.map((item) => item.data.id);
			}

			return [];
		});

		await store.loadServerStatesById(articleIds);
		let index = 1;
		const list: ReaderItem[] = [];
		this.positionsData.positions.forEach((position) => {
			if ('item' in position) {
				const sort = index++;

				const data = position.item.data;

				let article: ReaderItem | undefined;
				if (data.type === 'edition-brief') {
					data.publishedDate = this.publishDate;
					article = this.adapter.createEditionBrief(
						data,
						store,
						articleIds,
						this.editionData.attributes.title
					);
				} else {
					article = this.adapter.formatListItems([data], '', null, store).pop();
				}

				// TODO: Consider Reels handling
				if (article instanceof Article) {
					article.sortOrder = sort;
					list.push(article);
				}

				return;
			}

			if ('items' in position) {
				position.items.forEach((item) => {
					const sort = index++;
					const data = item.data;
					const [article] = this.adapter.formatListItems(
						[data],
						position.group?.data?.title || '',
						null,
						store
					);
					if (!article) {
						return;
					}
					// TODO: Consider Reels handling
					if (article instanceof Article) {
						article.sortOrder = sort;
						article.groupBy = position.group.data.title;
					}
					article.sourceUrl = `https://${data.publication}${data.url}`;
					list.push(article);
				});
			}
		});

		return list;
	}

	async loadFullArticle(
		article: Article,
		groupBy: string,
		sortOrder: number | undefined,
		store: Store
	): Promise<Article | null> {
		return this.adapter.loadItem(article, groupBy, sortOrder, store);
	}

	loadRelatedArticles(_article: Article) {
		return Promise.resolve([]);
	}

	getPath() {
		return `/digital-reader/${this.id}`;
	}
}
